/**
 * Phone buy bar on the product page (blocks/cirque-buy-bar.liquid).
 *
 * The bar has no cart logic of its own. Choosing a size in it presses the page's own size button,
 * and its Add to cart presses the page's own Add to cart, so prices, stock, the cart and the cart
 * drawer behave exactly as they do from the main buttons. The theme's product form already waits
 * for a size change to finish before it adds, so a quick "pick, then add" adds the size picked.
 *
 * The size choice is a small panel that slides up out of the bar, not the phone's own menu.
 */
class CirqueBuyBar extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    const toggle = this.querySelector('.cirque-buy-bar__toggle');
    const sheet = this.querySelector('.cirque-buy-bar__sheet');
    const add = this.querySelector('.cirque-buy-bar__add');
    if (!(toggle instanceof HTMLButtonElement) || !(sheet instanceof HTMLElement) || !(add instanceof HTMLButtonElement)) {
      return;
    }

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    toggle.addEventListener('click', () => (sheet.hidden ? this.#open() : this.#close()), { signal });
    add.addEventListener('click', () => this.#add(), { signal });

    sheet.addEventListener(
      'click',
      (event) => {
        const size = event.target instanceof Element ? event.target.closest('.cirque-buy-bar__size') : null;
        if (!(size instanceof HTMLButtonElement) || size.disabled) return;

        this.#pick(size.dataset.value ?? '');
        this.#show(size.dataset.value ?? '');
        this.#close();
      },
      { signal }
    );

    // A tap anywhere else, or Escape, puts the panel away
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (!sheet.hidden && event.target instanceof Node && !this.contains(event.target)) this.#close();
      },
      { signal }
    );
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && !sheet.hidden) {
          this.#close();
          toggle.focus();
        }
      },
      { signal }
    );

    // Follow the page's own size buttons, however they were changed
    document.addEventListener(
      'change',
      (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || !input.checked) return;
        if (this.#mainRadios().includes(input)) this.#show(input.value);
      },
      { signal, capture: true }
    );

    this.toggleAttribute('data-shown', true);
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #open() {
    const sheet = this.querySelector('.cirque-buy-bar__sheet');
    const toggle = this.querySelector('.cirque-buy-bar__toggle');
    if (!(sheet instanceof HTMLElement)) return;

    sheet.hidden = false;
    toggle?.setAttribute('aria-expanded', 'true');
    // Next frame, so the panel slides up rather than appearing in place
    requestAnimationFrame(() => sheet.classList.add('is-open'));
  }

  #close() {
    const sheet = this.querySelector('.cirque-buy-bar__sheet');
    const toggle = this.querySelector('.cirque-buy-bar__toggle');
    if (!(sheet instanceof HTMLElement) || sheet.hidden) return;

    sheet.classList.remove('is-open');
    toggle?.setAttribute('aria-expanded', 'false');

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.setTimeout(() => {
      if (!sheet.classList.contains('is-open')) sheet.hidden = true;
    }, reduced ? 0 : 280);
  }

  /**
   * Shows a size as the chosen one in the bar.
   * @param {string} value
   */
  #show(value) {
    const picked = this.querySelector('.cirque-buy-bar__picked');
    if (picked) picked.textContent = value;

    let available = true;
    for (const size of this.querySelectorAll('.cirque-buy-bar__size')) {
      if (!(size instanceof HTMLButtonElement)) continue;

      const chosen = size.dataset.value === value;
      size.setAttribute('aria-pressed', String(chosen));
      if (chosen) available = !size.disabled;
    }

    const add = this.querySelector('.cirque-buy-bar__add');
    if (add instanceof HTMLButtonElement) add.disabled = !available;
  }

  /** The size buttons on the page itself, not those in a quick-add pop-up. */
  #mainRadios() {
    return Array.from(document.querySelectorAll('.product-information variant-picker input[type="radio"]')).filter(
      (radio) => radio instanceof HTMLInputElement && !radio.closest('dialog')
    );
  }

  /** The page's own Add to cart, not the theme's bottom bar or a pop-up's. */
  #mainAddButton() {
    return Array.from(document.querySelectorAll('.product-information product-form-component [ref="addToCartButton"]')).find(
      (button) => button instanceof HTMLButtonElement && !button.closest('dialog, sticky-add-to-cart')
    );
  }

  /** @param {string} value */
  #pick(value) {
    const radio = this.#mainRadios().find((input) => input.value === value);
    if (radio instanceof HTMLInputElement && !radio.checked) radio.click();
  }

  #add() {
    this.#close();

    const button = this.#mainAddButton();
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    button.click();
  }
}

if (!customElements.get('cirque-buy-bar')) {
  customElements.define('cirque-buy-bar', CirqueBuyBar);
}
